import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const { pathname } = useLocation();

  useEffect(() => {
    if ((pathname.startsWith("/admin") || pathname.startsWith("/portal") || pathname.startsWith("/supplier")) && user === undefined) {
      api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(null));
    }
  }, [pathname, user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout API call failed (session cleared locally):", err?.message); }
    setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, login, register, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

const STAFF_ROLES = ["super_admin", "procurement_manager", "sales", "finance", "logistics"];

export function ProtectedRoute({ children, staff = false, loginPath = "/admin/login" }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-[var(--pg-accent)]" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to={loginPath} replace />;
  if (staff && !STAFF_ROLES.includes(user.role)) return <Navigate to={user.role === "supplier" ? "/supplier" : "/portal"} replace />;
  return children;
}
